import { useMemo } from 'react';
import styled from '@emotion/styled';
import { type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid';
import { type Chat, type ChatResponseType } from '@trinity/chat';
import { Table } from '@trinity/chat-components/components/Chat.Attachments/Table';
import { TextQueryHint } from '@trinity/chat-components/components/presentation/Message.QueryHint/index.js';
import { useAccountSelectionAction } from './hooks/useAccountSelectionAction';
import { BasicSystemUserMessage } from './Message.System.Basic';
import { MessageContainer } from './Message.System.Wrapper';

type Props = {
  queryIdentifier: string;
  response: Chat.QueryResponse<typeof ChatResponseType.AccountSearchResults>;
  disabled?: boolean;
};

export const AccountSearchResultsResponse = ({ response, disabled = false }: Props) => {
  const submitAccountSelection = useAccountSelectionAction();
  const results = response.data.results;

  const columns = useMemo<GridColDef[]>(() => {
    return [
      { field: 'accountId', headerName: 'Account Id', type: 'string', minWidth: 110, maxWidth: 240 },
      {
        field: 'select',
        headerName: 'Select',
        type: 'string',
        minWidth: 90,
        maxWidth: 90,
        sortable: false,
        renderCell: (params: GridRenderCellParams<Record<string, unknown>>) => {
          const accountId = params.row.accountId;
          const accountName = params.row.name;

          if (typeof accountId !== 'string' || typeof accountName !== 'string') {
            return null;
          }

          return (
            <StyledSelectButton
              disabled={disabled}
              onClick={() => {
                if (disabled) {
                  return;
                }

                submitAccountSelection({ id: accountId, name: accountName });
              }}
              value="View"
            />
          );
        },
      },
      { field: 'name', headerName: 'Name', type: 'string', minWidth: 180, maxWidth: 320 },
      {
        field: 'classification',
        headerName: 'Classification',
        type: 'string',
        minWidth: 160,
        maxWidth: 240,
      },
      { field: 'address', headerName: 'Address', type: 'string', minWidth: 260, maxWidth: 520 },
    ];
  }, [disabled, submitAccountSelection]);

  const rows = useMemo<Array<Record<string, unknown>>>(() => {
    return results.map((account) => ({
      accountId: account.id,
      select: 'View',
      name: account.name,
      classification: account.classification ?? '',
      address: account.address,
    }));
  }, [results]);

  return (
    <MessageContainer>
      <BasicSystemUserMessage>
        <Body>
          <Message>
            Sure, I found the following matching accounts. Select an account by clicking "View".
          </Message>

          {results.length > 0 ? (
            <Table
              tableId={`account-search-results-${response.data.search.name}-${results.length}`}
              columns={columns}
              rows={rows}
              title="account-search-results"
              showExportCsv={false}
              pinnedLeft={['accountId', 'select', 'name']}
            />
          ) : (
            <NoResults>No accounts were found for your search criteria.</NoResults>
          )}
        </Body>
      </BasicSystemUserMessage>
    </MessageContainer>
  );
};

const Body = styled.div({
  width: '100%',
});

const Message = styled.div({
  marginBottom: 15,
});

const StyledSelectButton = styled(TextQueryHint)({
  minWidth: 65,
  maxWidth: 65,
});

const NoResults = styled.div(({ theme }) => ({
  marginTop: 10,
  fontFamily: theme.fonts.regular,
}));
